import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { BaseComponent } from '../../../shared/utils/base.component';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ListTableComponent } from '../../../shared/components/list-table/list-table.component';
import { LeadDetailsService } from '../../../services/lead-details.service';
import {
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../shared/constants/static-data.constants';
import { TableRowActionsModel } from '../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../shared/interfaces/actions';
import { NzFormModule } from 'ng-zorro-antd/form';
import { LoggerService } from '../../../shared/services/logger.service';
import { SectionsService } from '../../../services/sections.service';
import { performValidation } from '../../../shared/utils/validation.util';
import { EnrollmentDataService } from '../../../services/enrollment-data.service';
import { AuthorizationUtility } from '../../../shared/authorization/auth.utility';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import { UserService } from '../../../services/user.service';
import { AuthorizeDirective } from '../../../shared/authorization/authorize.directive.';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';

@Component({
  selector: 'ppl-pace-details',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzGridModule,
    CommonModule,
    ButtonComponent,
    ListTableComponent,
    NzFormModule,
    NzSelectModule,
    AuthorizeDirective,
    NzDatePickerModule
  ],
  templateUrl: './pace-details.component.html',
  styleUrl: './pace-details.component.css',
})
export class PaceDetailsComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() pageId = '';
  @Input() tabId = '';
  @Input() program: any;
  @Input() readOnly = false;
  @Input() selectedLeadId: any;
  @Input() entry: any;
  @Input() set sectionData(data: string) {
    this.steps = data;
  }

  @Output() leadUpdated = new EventEmitter<boolean>();
  mode = 'read';
  paceDetailsForm!: FormGroup;
  paceData: any[] = [];
  isMobileView = false;
  orgList: any[] = [];
  leadStatus: any;
  approvedStatus: string = STATIC_DATA.APPROVED;
  cols: any;
  steps: any;
  isSectionEditable = true;
  mcoForRecords: any[] = [];
  actionButtons: TableRowActionsModel = new TableRowActions({ edit: true });
  statusList = [
    { label: 'Passed', value: 'Passed' },
    { label: 'Failed', value: 'Failed' },
  ];
  minStartDate?: Date;
  maxEndDate?: Date;
  isPatching = false;
  showEditError = false;
  formValues: any;
  isMCOUser = false;
  userDetails: any;

  constructor(
    private fb: FormBuilder,
    private leadDetailsService: LeadDetailsService,
    private logger: LoggerService,
    private sectionsService: SectionsService,
    private enrollmentService: EnrollmentDataService,
    private authUtility: AuthorizationUtility,
    private dateTimeFormatterService: DateTimeFormatterService,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.paceDetailsForm = this.fb.group({
      mcoName: [''],
      startDate: [],
      endDate: [],
      cbcResult: [null],
    });
    this.isSectionEditable = this.readOnly
      ? false
      : this.authUtility.isSectionEditable(
          this.pageId,
          this.tabId,
          'paceResults'
        );
    this.lookupService
      .getOrgData(this.program.program_code)
      .subscribe((data: any) => {
        if (data.status == 200) {
          this.orgList = data.responsedata.filter(
            (item: any) =>
              item.orgTypeLookup === 'MCO' && item.isPaceCBC === true
          );
        }
      });

    this.actionButtons.edit = true;
    this.getFromData();
    this.getPaceData();
    this.paceDetailsForm.get('endDate')?.valueChanges.subscribe(() => {
      this.updateMinStartDate();
    });
    this.paceDetailsForm.get('startDate')?.valueChanges.subscribe(() => {
      this.updateMaxEndDate();
    });
    this.userDetails = this.userService.getUserDetails();
    this.isMCOUser = this.userDetails?.usertype_lkp?.toLowerCase() === 'mco';

  }

  updateMinStartDate(): void {
    if (this.isPatching) return;
    const endDate = this.paceDetailsForm.get('endDate')?.value;
    if (endDate instanceof Date) {
      this.minStartDate = new Date(endDate);
    } else {
      this.minStartDate = undefined;
    }
  }

  disabledStartDate = (startValue: Date): boolean => {
    const endDate = this.paceDetailsForm.get('endDate')?.value;
    if (!startValue || !endDate || !(endDate instanceof Date)) {
      return false;
    }
    return startValue.getTime() > endDate.getTime();
  };

  disabledEndDate = (endValue: Date): boolean => {
    const startDate = this.paceDetailsForm.get('startDate')?.value;
    if (!endValue || !startDate || !(startDate instanceof Date)) {
      return false;
    }
    return endValue.getTime() <= startDate.getTime();
  };

  getPaceData() {
    this.enrollmentService
      .getPaceDetails(this.program.program_code, this.selectedLeadId)
      .subscribe((data: any) => {
        const userOrgId = this.userDetails?.belongsto_org;
        let paceList = data.responsedata || [];
        if (this.isMCOUser) {
          paceList = paceList?.filter((item: any) => item?.mcoId === userOrgId);
        }
        this.paceData = paceList;
        this.mcoForRecords = [];
        this.paceData?.forEach((item: any) => {
          this.mcoForRecords.push(item.mcoName);
        });
        this.paceData = this.addEditAction(this.paceData);
        this.getOrgList();
      });
  }

  getOrgList() {
    const userOrgId = this.userDetails?.belongsto_org;
    let filteredList = this.orgList;
    if (this.isMCOUser) {
      filteredList = filteredList?.filter((item: any) => item?.orgId === userOrgId);
    }
    this.orgList = filteredList?.map((item: any) => {
      return {
        ...item,
        disabled: this.shouldDisableMCO(item),
      };
    });
  }

  updateMaxEndDate(): void {
    if (this.isPatching) return;
    const startDate = this.paceDetailsForm.get('startDate')?.value;
    if (startDate instanceof Date) {
      this.maxEndDate = new Date(startDate);
    } else {
      this.maxEndDate = undefined;
    }
  }

  shouldDisableMCO = (option: any) => {
    return this.mcoForRecords.includes(option.orgName);
  };

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['pace_details'];
      }
    } catch (e) {
      this.logger.debug(
        'Error in getting section data and updating latest info',
        e
      );
    }
  }

  async getFromData() {
    try {
      if (this.entry) {
        const tempCols = await this.sectionsService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.LEAD_DETAILS_CONTACT_LIST_HEADERS,
          this.entry
        );
        this.cols = tempCols?.paceDetails?.columns;
      }
    } catch (e) {
      this.logger.debug('Error in getting table headers', e);
    }
  }

  action(event: any) {
    if (event && event.action === 'edit') {
      this.mode = 'edit';
      this.isPatching = true;
      const data = { ...event.data };
      this.formValues = event.data;
      if (typeof data.startDate === 'string' && data.startDate) {
        data.startDate = new Date(data.startDate);
      }
      if (typeof data.endDate === 'string' && data.endDate) {
        data.endDate = new Date(data.endDate);
      }
      this.paceDetailsForm.patchValue(data, { emitEvent: false });
      setTimeout(() => {
        this.isPatching = false;
        this.updateMinStartDate();
        this.updateMaxEndDate();
      });
      this.paceDetailsForm.get('mcoName')?.disable();
    }
  }

  add() {
    this.mode = 'add';
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.paceDetailsForm.reset();
    this.paceDetailsForm.get('mcoName')?.enable();
    this.showEditError = false;
  }

  addEditAction(data: any[]) {
    const seenMcoIds = new Set<number>();
    return data.map(item => {
      const isEditable = !seenMcoIds.has(item.mcoId);
      if (isEditable) seenMcoIds.add(item.mcoId);
      return {
        ...item,
        actions: {
          edit: isEditable
        }
      };
    });
  }

  save() {
    const data = this.paceDetailsForm.getRawValue();
    try {
      this.validationErrors = performValidation(
        data,
        this.secData,
        'Save',
        undefined,
        undefined,
        this.sectionsService.getFlow(),
        this.sectionsService.getEnrollmentType()
      );
    } catch (e) {
      this.logger.debug('Error in validations', e);
    }
    if (Object.keys(this.validationErrors).length === 0) {
      if (data?.cbcResult === this.formValues?.cbcResult 
        && new Date(data?.startDate).toDateString() === new Date(this.formValues?.startDate).toDateString() 
        && new Date(data?.endDate).toDateString() === new Date(this.formValues?.endDate).toDateString()) {
        this.showEditError = true;
      }
      else {
      this.showEditError = false;
      const payload = {
        providerId: this.selectedLeadId,
        mcoId: this.orgList.find((org) => org.orgName === data.mcoName)?.orgId,
        mcoName: data.mcoName,
        cbcResult: data.cbcResult,
        startDate: data.startDate
          ? this.dateTimeFormatterService.formatDateToUSDateString(
              new Date(data.startDate)
            )
          : null,
        endDate: data.endDate
          ? this.dateTimeFormatterService.formatDateToUSDateString(
              new Date(data.endDate)
            )
          : null,
        programCode: this.program.program_code,
      };
      this.enrollmentService
        .savePaceRecord(payload, true)
        .subscribe((data: any) => {
          if (data.status === 200) {
            this.getPaceData();
            this.cancelEdit();
          }
        });
    }
    }
  }
}
